import React, { useState, useRef } from 'react';
import { apiPost } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Upload, FileText, X, Loader2, CheckCircle2,
  DollarSign, TrendingUp, Target, Users, Settings, BarChart3,
} from 'lucide-react';

const ICON_MAP = {
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'presentation': FileText,
  'users': Users,
  'settings': Settings,
  'file-text': FileText,
  'target': Target,
  'bar-chart': BarChart3,
};

export function DocumentPrompt({ suggestion, onUploaded, onDismiss }) {
  const [showUpload, setShowUpload] = useState(false);
  if (!suggestion) return null;

  const Icon = ICON_MAP[suggestion.icon] || FileText;

  if (showUpload) {
    return <DocumentUploadModal docType={suggestion.doc_type} label={suggestion.label} onClose={() => setShowUpload(false)} onUploaded={(result) => { setShowUpload(false); onUploaded(result); }} />;
  }

  return (
    <div className="mx-auto max-w-md" data-testid="document-prompt">
      <div className="rounded-xl border border-blue-500/20 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">{suggestion.label}</p>
            <p className="text-xs text-muted-foreground mb-3">{suggestion.prompt}</p>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowUpload(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-3" data-testid="doc-upload-btn">
                <Upload className="w-3 h-3 mr-1" /> Upload
              </Button>
              <button onClick={onDismiss} className="text-xs text-muted-foreground hover:text-foreground" data-testid="doc-dismiss-btn">Not now</button>
            </div>
          </div>
          <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

export function DocumentUploadModal({ docType, label, onClose, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const inputRef = useRef(null);

  const handleFile = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx', 'doc', 'txt', 'md', 'csv'].includes(ext)) {
      toast.error('Supported formats: PDF, DOCX, TXT, MD, CSV');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await apiPost(`/company/documents/upload?doc_type=${docType}`, formData);
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.filename} processed — ${data.key_facts_count} insights extracted`);
        onUploaded(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Connection error');
    }
    setUploading(false);
  };

  return (
    <div className="mx-auto max-w-md" data-testid="document-upload-modal">
      <div className="rounded-xl border border-border bg-card p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm">Upload: {label}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-blue-500 bg-blue-500/5' : selectedFile ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border hover:border-blue-500/40'}`}
          data-testid="doc-dropzone"
        >
          <input ref={inputRef} type="file" className="hidden" accept=".pdf,.docx,.doc,.txt,.md,.csv" onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
          {selectedFile ? (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-medium">{selectedFile.name}</span>
              <span className="text-xs text-muted-foreground">({(selectedFile.size / 1024).toFixed(0)} KB)</span>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Drop your file here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, TXT, MD, CSV (max 10MB)</p>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 text-xs">Cancel</Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="doc-submit-btn">
            {uploading ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Processing...</> : <><Upload className="w-3 h-3 mr-1" /> Upload &amp; Analyze</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
